import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core';

import { asyncScheduler, Observable } from 'rxjs';
import * as pdfMake from "pdfmake/build/pdfmake";
import { TDocumentDefinitions, Content, ContextPageSize } from 'pdfmake/interfaces';

import { decodeKeys,  isDefaultKey, keySignature, UNCDump, UNCRecord, UNCRecordType, UNCType } from 'src/app/model/uncsettings';

import { getMidiNoteForCode } from 'src/app/helpers/notes';
import { GlobalSettings } from 'src/app/model/global-settings';
import { DeviceInfo, EMEO_SAX_V1, HardwareTypes, INSTRUMENT_TYPES, InstrumentType } from 'src/app/model/device-info';
import { PlatformLocation } from '@angular/common';
import { MidiCCTypes } from 'src/app/model/midi-cctypes';

import { DateTime } from 'luxon';

import Vex from 'vexflow'
import { Backup } from 'src/app/model/backup';
import { PressureIndicatorComponent } from 'src/app/svg/pressure-indicator/pressure-indicator.component';
import { PitchIndicatorComponent } from 'src/app/svg/pitch-indicator/pitch-indicator.component';
import { EMEO_LOGO } from './emeo';
import { findDeviceInfo } from 'src/app/model/instruments/device-definitions';
import { InstrumentInfoEx } from 'src/app/model/instrument-info-ex';
import { EMEO_SAX_V1_INFO_EX } from 'src/app/model/instruments/emeo-sax-v1-ex';
import { CloseCallback } from '../flasher/flasher.component';
import { FeatureSupportService } from 'src/app/services/feature-support.service';
import { PDF_FONTS_VFS } from 'src/app/helpers/fonts';

// import { EMEO_SAX_V1_INFO } from 'src/app/model/instruments/emeo-sax-v1';
// import { EmeoSaxophoneV1Component } from 'src/app/svg/emeo-saxophone-v1/emeo-saxophone-v1.component';

export type IPropertySet = { [ key: string ] : any } ; 

export interface IProgressWithInfo {
  progress(progress: number, info: string) : void;
  error?(reason: any) : void;
}

export type PreparationCallback = (callback: IProgressWithInfo) => Promise<Backup>;

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss']
})
export class ReportComponent implements OnInit {

  constructor(
    protected fs: FeatureSupportService,
    protected platformLocation: PlatformLocation,
    protected cd: ChangeDetectorRef) { 

  }

  closeCallback: CloseCallback | undefined = undefined;

  ngOnInit(): void {
  }

  @Input()
  backup: Backup | undefined = undefined;

  @Input() 
  rendering: boolean = false;

  renderOnlyCustomFingerings: boolean = false;

  toggleRenderOnlyCustomFingerings(e: Event) {
    this.renderOnlyCustomFingerings = (e.target as HTMLInputElement).checked;
  }

  async prepareData(prepareCallback: PreparationCallback) {

    this.resetProgress();

    this.backup = await prepareCallback({
      progress: (progress, info) => {
        this.updateProgress(progress, 0, info);
      },
    });

    this.updateProgress(1, 0, "Done");
  }

  async cancelReport() {
    if (this.closeCallback) {
      await this.closeCallback()
    }
  }

  async renderReport() {
    if (this.backup) {
      await this.renderPdf(this.backup, 0);
    }
  }

  @ViewChild('tempHtml', { read: ViewContainerRef }) tempHtml: any;

  @ViewChild('tempScore', { read: ElementRef }) tempScore: any;

  private async renderComponent(component: any, data: IPropertySet ) {

    return new Promise<string>((resolve, reject) => {

      this.tempHtml.clear();
      const componentRef = this.tempHtml.createComponent(component);
  
      Object.entries<any>(data).forEach( (v: [string, any]) => {
        componentRef.setInput(v[0], v[1]);
      })

      asyncScheduler.schedule(() => {
        const htmlString = componentRef.location.nativeElement.innerHTML;
        componentRef.destroy();
        
        resolve(htmlString);
      });
  
    });
  }

  private async renderInstrument(instrument: InstrumentInfoEx): Promise<string> {
    return this.renderComponent(instrument.instrumentComponent, { printing: true });
  }

  private async renderFingering(instrument: InstrumentInfoEx, uncType: UNCType, pressed: string[], isOverride: boolean = true): Promise<string> {

    return this.renderComponent(instrument.fingeringDiagram, {
      pressed: pressed,
      uncType: uncType,
      isOverride: isOverride
    });
  }

  private async renderPitchScale(detuning: number): Promise<string> {

    return this.renderComponent(PitchIndicatorComponent, {
      detuning: detuning,
    });

  }

  private async renderPressureScale(resistance: number): Promise<string> {

    return this.renderComponent(PressureIndicatorComponent, {
      resistance: resistance
    });

  }

  async renderPdf(backup: Backup, processed: number = 0) {

    let instrument = findDeviceInfo(backup.device?.hardware || EMEO_SAX_V1) || EMEO_SAX_V1_INFO_EX;

    let svgInstrument = await this.renderInstrument(instrument);

    this.updateProgress(0, processed, "Preparing Layout")
    this.rendering = true;

    let tempDate = DateTime.fromJSDate(new Date(backup.date)) || DateTime.now();
    let circuitId = backup.device?.hardware.circuitSerialNumber || "Unknown";
    let reportDate = tempDate.toLocaleString(DateTime.DATE_FULL, { locale: "en-US"});
    let pl: any = this.platformLocation as any

    let prefix = (this.fs.electronMode ? '.' : '..');
    let url = new URL(prefix + '/assets/Flat Symbol Font.ttf', pl.href);

    DateTime.now().toLocaleString(DateTime.DATE_FULL);

    const fonts = {
      Roboto: {
        normal:
          "Roboto-Regular.ttf",
        bold:
          "Roboto-Medium.ttf",
        italics:
          "Roboto-Italic.ttf",
        bolditalics:
          "Roboto-MediumItalic.ttf"
      },

      Accidentials: {
        normal:
          "Flat Symbol Font.ttf"
      }
    };

    (pdfMake as any).vfs = PDF_FONTS_VFS;

    this.updateProgress(0.1, processed, "Preparing Layout");

    let docDefinition: TDocumentDefinitions = {
    
      // a string or { width: number, height: number }
      pageSize: 'A4',
  
      // by default we use portrait, you can change it to landscape if you wish
      pageOrientation: 'portrait',
  
      // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
      pageMargins: [ 40, 90, 40, 60 ],
    
      pageBreakBefore: function(cur, next, next_page, prev) {
        var pb = false;
        if ((cur.headlineLevel === 1) || (cur.headlineLevel === 0))	{		// Each table row has headlineLevel 1, last row has headlineLevel 2
          if (!next.find(node => node.headlineLevel) && next_page.length) {	// If last row, test further
             let next_row = next_page.find(node => node.headlineLevel);
            pb = (next_row && (next_row.headlineLevel === 2)) || true;	// Page break if 1st row on next page is last row of table
          }
        }
        return pb;
      },

      info: {
          title: 'EMEO Configuration Report',
          author: 'emeomusic.com',
          subject: reportDate,
          keywords: 'EMEO',
      },
  
      styles: {
        header: {
          marginTop: 15,
          fontSize: 22,
          bold: true
        },

        subheader: {
          marginTop: 10,
          fontSize: 16,
          bold: true
        },

        annotation: {
          fontSize: 8
        }
      },

      background: function(currentPage: number, pageSize: ContextPageSize) : Content {
          
          const headerHeight = 80;
          const footerHeight = 40;
          const boxColor = '#000';
          
          let image: any[] = [];

          if (currentPage === 1) {
            image.push(
              {
                // if you specify width, svg will scale proportionally
                svg: svgInstrument,
                // '<svg width="100%" height="100%" viewBox="0 0 479 1965" version="1.1"  xmlns="http://www.w3.org/2000/svg"  xmlns:xlink="http://www.w3.org/1999/xlink"  xml:space="preserve"  xmlns:serif="http://www.serif.com/"  style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:1.5;"> <style> g { fill:#fff; stroke:#aaa; stroke-width:2pt; } #Keys, #LeftPalmKeys, #RightPalmKeys, #PinkyPad { fill: lightgrey; cursor: pointer; } .unstroked path { stroke:none; } </style>    <g id="Instrument"> <g id="Corpus"> <path d="M155.354,5.692c19.884,-5.31 41.641,-6.244 63.428,-0.133l1.088,65.545c11.265,-0.005 22.128,1.177 27.471,3.01c2.904,0.996 1.524,8.18 4.588,7.987c5.331,-0.336 28.089,-0.985 31.286,-0.137c3.494,0.926 1.811,9.86 5.421,9.683c11.004,-0.54 22.398,-3.823 25.249,-1.238c3.643,3.301 3.918,11.315 0.849,15.328c-4.276,5.592 -24.647,20.405 -29.583,18.455c-5.718,-2.258 -15.058,-2.921 -18.072,-2.714c-3.165,0.217 -1.585,-10.691 -1.585,-10.691c-6.612,10.705 -15.572,13.586 -25.652,12.74l0.792,138.184c6.478,11.748 3.127,50.265 2.604,61.314c-0.12,2.529 -5.327,2.476 -5.743,4.974c-0.904,5.429 0.316,27.597 0.316,27.597c1.45,-2.555 4.88,132.213 4.901,164.618c4.286,1.595 8.611,4.478 10.74,7.001c8.313,-0.361 32.2,0.418 32.2,0.418l3.917,18.458c0,0 -23.699,0.409 -34.57,1.777c-1.766,7 -5.877,10.865 -9.007,12.508c-0.301,91.661 6.425,535.193 6.425,535.193l53.676,-0.815l5.192,12.501l-7.095,17.843l-15.91,-0.433l-10.675,-9.41l-23.761,-1.26l19.865,793.209l0,15.668l-203.749,0.45l-0.053,-36.556c15.731,-16.176 7.221,-23.975 -2.741,-27.869c1.218,-43.399 -2.112,-72.648 10.12,-85.921l16.397,-12.154l12.263,-290.689l3.569,-292.408l27.249,-973.496l-0.899,-127.208c5.456,-1.574 11.311,-2.91 18.583,-3.395l0.906,-67.934Z"/> <path d="M265.48,1700.41l45.064,-3.485l18.266,7.488l10.412,-250.626l-38.023,13.861l-2.083,20.955l-4.84,5.058l2.273,18.495l21.36,1.686l-1.585,35.81l-19.422,0.374l-0.703,30.409l14.484,64.553l8.527,5.003l-0.263,19.546l-9.362,7.902l-37.506,-6.06l-6.599,29.031Z"/> <path d="M240.191,1259.92c-0.774,7.214 4.704,19.128 9.323,21.676c2.68,1.479 8.581,2.16 8.581,2.16l-0.214,-8.576l49.925,-4.575l-0,-19.51l-42.326,-3.959c0,0 -14.059,-3.284 -18.379,-1.35c-4.319,1.933 -6.343,8.846 -6.91,14.134Z"/> <ellipse cx="217.626" cy="1342.15" rx="52.783" ry="59.552"/> <path d="M307.806,1404.78l-29.53,-2.924c-0,-0 -5.511,-9.644 -12.796,-11.26c-4.901,-1.088 -13.588,3.524 -17.298,9.991c-3.711,6.467 -5.356,19.753 -3.882,25.892c1.306,5.439 12.131,12.527 17.662,11.695c5.605,-0.844 15.152,-12.379 15.152,-12.379l32.922,0.219l-2.23,-21.234Z"/> <path d="M308.93,1327.7c-0,-0 -70.717,7.212 -88.218,9.995c-37.106,5.901 -67.456,13.487 -15.792,12.142c15.272,-0.397 108.228,-11.164 108.228,-11.164l-4.218,-10.973Z"/> <path d="M261.381,1568.14l47.886,8.887l-8.494,-482.082l7.442,-5.497l-7.682,-6.017l-0.339,-118.288l-12.279,-1.155l34.614,-4.694l3.342,621.902c0,-0 8.523,8.151 7.9,12.688c-0.623,4.537 -8.349,12.569 -13.094,13.513c-4.746,0.943 -15.379,-7.85 -15.379,-7.85l-47.518,1.272c-0,-0 -7.585,11.888 -16.338,9.626c-4.536,-1.172 -10.855,-9.472 -11.055,-15.933c-0.36,-11.555 4.045,-30.135 10.094,-33.203c10.037,-5.092 20.9,6.831 20.9,6.831Z"/> <path d="M328.126,914.26c0,-0 1.736,13.706 0.993,17.21c-0.461,2.17 -3.237,3.893 -5.454,3.814c-2.238,-0.08 -4.955,-4.314 -7.973,-4.292c-3.318,0.024 -11.938,4.436 -11.938,4.436l-14.697,21.266l0.147,30.4l55.247,16.998l24.546,-20.455c-0.084,-10.135 -1.916,-21.28 -4.942,-33.117l-8.999,-32.781l-26.93,-3.479Z"/> <path d="M87.855,1095.56c-23.679,-4.038 -22.133,-66.914 -21.419,-102.324c0.671,-33.243 3.141,-76.116 27.181,-80.065c3.393,20.65 -1.021,172.823 -5.762,182.389Z"/> <path d="M386.067,689.144c-0.728,4.319 -9.878,1.708 -13.128,0.587c-3.057,-1.055 -4.894,-4.439 -6.374,-7.315c-2.49,-4.833 -2.656,-16.635 -8.562,-21.688c-7.779,-6.657 -25.74,-12.71 -38.113,-18.254c-11.899,-5.331 -28.029,-7.154 -36.123,-15.007c-8.094,-7.853 -14.784,-29.048 -12.442,-32.112c2.342,-3.064 17.615,9.246 26.496,13.727c12.246,6.18 34.532,18.666 46.981,23.352c8.771,3.302 21.682,3.143 27.71,4.762c3.154,0.848 7.631,1.792 8.456,4.952c2.259,8.658 6.438,39.065 5.099,46.996Z"/> <g> <g> <path d="M273.709,1814.73c6.313,1.286 15.876,5.84 22.376,2.114c9.056,-5.192 16.503,-27.247 13.951,-51.436c-1.875,-17.781 -6.16,-42.243 -11.682,-55.818c-4.199,-10.32 -14.849,-22.668 -21.454,-25.63c-6.025,-2.702 -17.206,1.328 -18.181,7.858c-3.115,20.861 -3.008,96.824 -0.51,117.309c0.666,5.453 10.117,4.506 15.5,5.603Z"/> <path d="M275.572,1704.92c8.84,8.356 16.655,37.685 16.766,54.331c0.27,40.45 -7.111,35.102 -14.797,45.531c-9.467,12.847 -3.832,-26.079 -3.832,-26.079c5.567,-7.493 5.88,-18.243 0,-32.832" style="fill:none;"/> </g> </g> <path d="M266.724,1746.19c0,0 -5.335,4.595 -9.941,4.836c-4.626,0.242 -13.74,-7.454 -14.104,-18.777c-0.17,-5.281 5.78,-14.761 5.78,-14.761l80.664,12.649l-8.804,-722.679l16.057,-35.908l4.472,759.466c-0,-0 5.029,3.787 5.029,11.237c0,9.77 -9.697,12.553 -9.697,12.553l-69.456,-8.616"/> <ellipse cx="221.634" cy="1663.69" rx="66.28" ry="69.806"/> <path d="M327.959,1634.57c0,-0 -44.978,17.881 -62.479,20.664c-7.38,1.173 -17.963,0.299 -23.877,1.688c0,0 -26.423,18.829 13.442,13.064c40.636,-5.877 73.061,-23.384 73.061,-23.384l-0.147,-12.032Z"/> <g> <path d="M273.709,1881.56l61.341,1.679c0,-0 5.994,6.735 10.373,7.058c8.391,0.621 15.394,-6.088 16.683,-11.177c1.254,-4.949 -6.001,-13.899 -6.001,-13.899l-0.424,-66.236c-0,-0 4.524,-3.267 4.673,-8.413c0.123,-4.199 -7.244,-7.679 -7.244,-7.679l-0.72,-104.428c0,-0 17.452,-1.933 22.201,-4.614c4.372,-2.469 7.844,-6.856 6.601,-10.924c-2.546,-8.328 -27.616,-20.567 -27.616,-20.567l-1.963,-169.233c0,-0 23.158,-7.161 28.079,-11.418c3.258,-2.819 3.49,-14.151 -0.109,-16.521c-7.934,-5.225 -26.794,-11.334 -26.794,-11.334l-2.754,-393.264c0,-0 21.141,-2.737 26.341,-5.261c3.142,-1.525 6.307,-8.798 3.337,-10.634c-4.551,-2.814 -29.685,-5.999 -29.685,-5.999l-0.112,-18.873l13.485,-4.535l-0.967,-10.275l-16.381,-1.214l0.006,-19.806l-9.495,0.793l3.628,492.681c0,-0 -28.927,4.844 -39.074,1.684c-2.843,-0.885 -7.532,2.421 -7.925,4.433c-0.393,2.012 4.713,8.62 8.041,9.513c7.923,2.127 38.746,1.495 38.746,1.495l-0.04,190.103c0.18,1.844 -18.122,0.893 -22.147,0.272c-1.549,-0.239 -2.871,4.438 -1.676,5.454c6.032,5.131 24.406,7.624 24.406,7.624l0.325,51.355c8.559,9.734 5.71,21.835 -4.668,25.404c-11.765,-1.459 -19.395,-2.406 -27.948,-3.467l2.312,14.263l10.316,0.155l15.13,9.279l0.108,7.658c0,-0 -5.608,4.26 -5.618,8.865c-0.007,3.307 5.307,9.489 5.307,9.489l1.76,63.81l-59.856,-16.302l-3.982,33.006Z"/> <path d="M351.334,1656.85c0,-0 -1.592,4.971 -3.502,6.367c-1.909,1.396 -7.956,2.011 -7.956,2.011" style="fill:none;"/> <path d="M342.339,1678.8l10.051,-0.337" style="fill:none;"/> <path d="M339.98,1474.59l11.633,-1.465" style="fill:none;"/> <path d="M353.787,1446.08c0,0 2.836,2.834 2.53,4.538c-0.981,5.454 -16.125,6.848 -16.125,6.848" style="fill:none;"/> </g> <path d="M206.082,1094.9l3.08,13.627l14.419,-0.119l3.433,-13.205l-20.932,-0.303Z"/> <path d="M250.152,263.582c-1.964,11.091 -1.073,50.667 -0.072,62.394c0.283,3.328 3.257,6.786 6.073,7.97c2.817,1.184 10.29,-4.445 10.824,-0.866c5.778,38.71 18.529,193.231 23.844,233.124c0.449,3.364 4.865,5.06 8.048,6.237c7.171,2.651 26.514,4.655 34.975,9.67c7.402,4.387 12.218,21.02 15.792,20.42c3.574,-0.599 5.802,-12.131 4.375,-17.792c-1.413,-5.608 -6.173,-11.688 -11.5,-13.939c-7.917,-3.346 -27.257,-6.114 -34.729,-12.36c-7.472,-6.245 -10.761,-17.485 -11.539,-27.349c-2.448,-31.037 -0.756,-121.788 -3.146,-158.871c-1.386,-21.491 -7.27,-47.481 -11.196,-63.628c-2.794,-11.491 -9.023,-25.061 -12.362,-33.255c-2.222,-5.452 -4.443,-13.95 -7.674,-15.909c-3.232,-1.96 -10.991,0.075 -11.713,4.154Z"/> <path d="M205.619,446.189c-2.105,3.233 -1.611,9.868 -0.857,12.962c0.613,2.515 2.833,5.152 5.383,5.599c5.175,0.908 20.422,1.153 25.669,-0.152c3.114,-0.774 2.812,-6.529 5.809,-7.678c3.641,-1.396 16.039,-0.699 16.039,-0.699l0.207,-12.319c-0,0 -15.04,2.376 -20.062,1.507c-3.978,-0.689 -6.671,-5.781 -10.073,-6.724c-3.339,-0.924 -7.058,-0.043 -10.339,1.07c-3.686,1.251 -9.67,3.201 -11.776,6.434Z"/> <path d="M292.3,890.236l37.357,-0.089c-0,0 0.513,-11.889 3.932,-14.981c5.158,-1.932 15.278,-6.578 18.27,-1.781c3.417,5.48 1.318,35.725 1.318,35.725c-0,-0 21.154,3.347 19.298,9.656c-6.002,20.414 -42.549,61.188 -54.735,96.747c-4.142,2.059 -4.685,24.423 -4.672,31.643c-6.925,0.455 -10.78,-0.126 -14.724,-1.181c-1.019,-14.039 -1.311,-50.69 -1.291,-60.221c17.599,-3.242 17.227,-7.181 19.6,-9.013c20.294,-33.388 27.194,-41.882 31.729,-45.983l1.492,-8.686c0,0 -24.764,-0.466 -31.296,-2.778c-4.277,-1.514 -7.996,-6.796 -7.892,-11.091c-5.246,-0.039 -20.711,-0.154 -20.711,-0.154l2.325,-17.813Z"/> <path d="M273.195,1962.44l0.514,-39.357l-203.749,0.243l-0.053,29.646c-0,5.359 4.351,9.711 9.71,9.711l193.578,-0.243Z"/> <path d="M37.009,1476.97c0,0 -6.79,4.654 -6.941,10.37c-0.109,4.156 5.415,12.094 12.948,13.842c4.214,0.977 7.606,-4.634 12.116,-5.394c5.911,-0.996 22.671,-0.493 22.671,-0.493l0.297,-10.546c-14.477,-4.633 -27.943,-6.621 -41.091,-7.779Z"/> <path d="M78.1,1484.75c0,-0 -33.324,-4.776 -41.116,-7.788c-3.583,-1.385 -5.892,-6.492 -5.447,-9.349c0.445,-2.857 5.891,-6.47 5.891,-6.47l0.364,-53.777c-0,0 33.16,-1.62 40.914,-0.141c3.477,0.664 6.446,8.934 3.513,10.916c-3.925,2.651 -27.066,4.994 -27.066,4.994l-0.673,35.946c0.796,2.791 4.413,4.868 9.873,6.478c4.628,1.365 10.579,2.395 17.258,3.24l-3.511,15.951Z"/> <g> <rect x="273.709" y="1907.2" width="31.28" height="55.232"/> <circle cx="287.672" cy="1923.61" r="4.653"/> <circle cx="287.672" cy="1945.67" r="4.653"/> </g> <path d="M160.811,203.827l-0.343,15.386l26.101,23.44c7.181,5.793 11.561,16.734 13.911,29.692l0.055,20.102l-5.925,7.213l-0.959,12.091l34.544,21.648l14.917,-10.868l0.348,-54.51c0.041,-6.426 -4.655,-5.584 -4.526,-12.448c-0,-0 1.086,-30.391 1.281,-45.111c0.249,-17.256 -15.133,-26.109 -24.586,-26.716c-9.452,-0.608 -21.672,6.04 -31.908,18.155c-3.205,3.793 -22.91,1.926 -22.91,1.926Z"/> <g id="Covers"> <ellipse cx="214.169" cy="1830.13" rx="75.18" ry="84.257"/> <ellipse cx="223.685" cy="1477.26" rx="58.842" ry="61.979"/> <ellipse cx="247.101" cy="1191.44" rx="52.783" ry="62.336"/> <ellipse cx="247.371" cy="906.477" rx="47.135" ry="53.6"/> <ellipse cx="247.371" cy="761.23" rx="47.135" ry="51.777"/> <ellipse cx="245.371" cy="639.984" rx="47.135" ry="51.777"/> <ellipse cx="198.26" cy="343.367" rx="37.554" ry="38.232"/> <ellipse cx="247.371" cy="906.477" rx="47.135" ry="53.6"/> </g> <path d="M294.134,1466.37c-15.46,0.498 -56.38,6.207 -73.815,9.738c-36.968,7.488 -67.086,16.368 -15.322,12.809c15.301,-1.052 65.965,-6.694 83.195,-11.124c3.809,-0.979 9.874,-11.55 5.942,-11.423Z"/> <path d="M36.646,1758.34l-0.073,58.785c39.608,13.188 69.907,12.824 103.237,10.352c33.301,-2.998 65.146,-2.877 96.004,-0.64c-29.81,9.409 -61.947,14.693 -95.596,17.295c-41.503,3.666 -76.557,0.046 -104.557,-11.714l0.003,55.825l52.599,-15.342c-1.282,-0.64 5.684,-13.422 12.863,-13.555c11.282,-0.21 18.007,17.96 18.185,27.545c0.178,9.584 -6.734,22.887 -11.959,25.971c-5.225,3.084 -18.681,-9.225 -18.681,-9.225l-49.366,3.686c0,-0 -7.205,6.921 -11.64,6.645c-11.429,-0.709 -15.403,-6.676 -16.593,-14.47c-0.547,-3.583 7.018,-10.876 7.018,-10.876l0.331,-59.46c0,0 -9.65,-3.017 -8.229,-11.528c0.845,-5.06 10.206,-5.442 10.206,-5.442l1.76,-66.622c-0,0 -12.091,-4.125 -10.96,-12.903c0.657,-5.094 9.001,-7.295 9.001,-7.295l3.077,-62.514c0,-0 -5.509,-3.909 -5.971,-6.57c-0.463,-2.66 -0.804,-7.789 1.215,-9.501c2.019,-1.712 9.74,-0.623 9.74,-0.623c0,-0 -7.265,-12.337 -7.976,-16.986c-0.601,-3.927 2.728,-11.842 6.23,-13.72c4.301,-2.308 20.663,-0.319 20.663,-0.319l51.403,-11.251l0.925,-11.748l5.755,-3.095c-0,-0 22.869,9.94 24.449,26.954c1.848,19.91 -10.636,30.594 -10.636,30.594"/> <ellipse cx="254.105" cy="1029.76" rx="52.293" ry="62.571"/> <path d="M178.515,1087.8l1.068,6.716l72.834,1.055l50.541,-0.768c0,0 2.663,0.278 4.1,-1.29c1.318,-1.439 1.654,-4.701 1.699,-5.388c0.267,-4.086 -8.224,-4.688 -8.224,-4.688l-2.883,-118.527l-8.593,0.378l1.484,118.909l-112.026,3.603Z"/> <path d="M128.484,1027.56l73.048,-2.566c38.944,-1.247 60.381,-1.24 63.455,0.334c1.974,1.012 -3.629,2.67 -17.036,5.058c-10.355,1.845 -28.171,3.652 -42.353,4.517c-15.187,0.926 -49.728,1.667 -49.728,1.667l-29.476,1.388l-2.222,111.219c-0,0 4.922,5.303 4.761,12.002c-0.11,4.545 -7.82,7.452 -7.82,7.452l-0.41,8.819l-13.061,0.038l-0.339,-9.564c0,0 -8.298,-3.568 -7.159,-10.154c1.181,-6.828 8.793,-10.346 8.793,-10.346l0.973,-380.732c0,0 -7.531,-4.81 -7.431,-7.057c0.099,-2.247 8.03,-6.424 8.03,-6.424l-0.33,-23.324c-0,0 -7.793,-11.702 -7.353,-16.005c0.441,-4.303 9.993,-9.814 9.993,-9.814l2.044,-51.99l9.373,-0.282l0.978,-34.944c0,0 10.722,-2.317 11.613,-5.308c1.133,-3.804 0.192,-14.71 -1.234,-18.006c-0.997,-2.306 -9.644,-2.938 -9.644,-2.938l7.386,-263.885l-1.248,44.601l-20.297,24.158l-16.622,390.088l-5.949,351.816l19.718,10.046"/> <path d="M128.484,1027.56l-3.679,-366.635" style="fill:none;"/> <path d="M96.698,1467.53l0,-27.424l-5.529,-8.273c0,-0 3.401,-18.089 -2.436,-23.254c-6.181,-5.469 -34.65,-9.563 -34.65,-9.563" style="fill:none;"/> <path d="M167.324,1276.64c-1.348,6.536 -12.613,23.15 -17.904,26.4c-4.392,2.698 -9.082,-5.962 -13.838,-6.903c-4.756,-0.941 -11.107,2.836 -14.699,1.257c-3.591,-1.578 -6.619,-6.746 -6.851,-10.729c-0.233,-3.983 5.456,-13.17 5.456,-13.17l0.519,-48.918l-20.979,-3.943l-2.003,161.855l-8.179,10.319l-39.674,0.706l-1.458,-13.68l30.892,2.046l7.498,-7.239l1.625,-52.369l-42.01,-1.565l-2.563,-18.414l44.842,-2.281l0.317,-25.138l-9.695,4.269l-31.765,0.065l-1.939,-10.037l17.776,1.065l6.371,-5.83c-0,0 -0.011,-19.569 -0.876,-24.966c-0.451,-2.814 -2.293,-5.679 -4.763,-7.1c-3.88,-2.233 -18.065,-6.616 -18.065,-6.616l-1.249,-17.064c0,0 12.516,4.633 17.033,3.727c4.272,-0.858 9.036,-4.53 9.623,-8.847c1.342,-9.88 -1.224,-40.796 -0.453,-51.061c0.306,-4.075 3.865,-6.811 5.078,-10.526c1.212,-3.716 2.858,-9.076 -0.201,-11.515c-3.643,-2.905 -15.492,-5.443 -19.481,-5.663c-1.719,-0.095 -2.353,2.712 -2.714,4.395c-1.922,8.951 -8.817,49.31 -8.817,49.31l-25.865,-8.855c0,0 11.723,-38.767 16.1,-47.989c1.791,-3.776 6.047,-6.601 10.159,-7.346c6.278,-1.138 21.674,0.905 27.509,0.517c2.667,-0.177 7.363,-0.174 7.497,-2.844c2.239,-44.552 5.932,-264.469 5.932,-264.469c0,-0 -8.248,-11.051 -8.199,-16.836c0.049,-5.784 8.493,-17.872 8.493,-17.872c0,0 12.295,-338.585 12.591,-411.068c-7.078,-7.86 -10.878,-19.066 -11.379,-26.431c-0.716,-10.513 5.836,-7.477 5.3,-15.351c-0.221,-3.234 -2.122,-6.454 -1.422,-9.619c0.776,-3.513 4.921,-10.477 9.143,-11.408c7.766,-1.714 37.453,1.124 37.453,1.124l4.128,59.79c0,0 -13.271,0.404 -16.724,1.741c-2.876,1.114 -3.651,7.085 -5.675,9.412c-2.202,2.531 -6.258,4.447 -6.258,4.447l-13.494,693.533c-0,-0 6.252,5.329 6.591,8.071c0.338,2.742 -4.56,8.38 -4.56,8.38l0.588,21.052l7.809,5.879l1.873,11.245l-18.749,3.775l-0.755,42.118l19.324,0.59l4.207,-100.77l110.754,-0.911l3.983,7.108l-2.198,4.912l-94.043,1.022l0.619,66.061l120.319,-4.634l18.48,0.048c15.166,0.36 4.232,8.125 -25.865,10.749l-112.338,3.364l0.036,12.6l9.391,5.465l-10.828,7.052l1.427,42.238c0,-0 16.681,-2.596 21.979,-0.551c5.021,1.938 10.9,7.55 9.813,12.821Z"/> <path d="M260.167,570.799c7.344,-0.283 18.358,-0.594 23.226,-4.009c4.868,-3.416 6.52,-10.489 5.983,-16.486c-3.046,-34.01 -12.695,-38.412 -18.849,-45.858c-4.115,-2.498 -11.996,-3.334 -15.257,-6.848c-3.261,-3.514 -3.91,-9.034 -4.309,-14.237c-0.765,-9.948 1.789,-30.551 -0.053,-41.154c-1.261,-7.256 -10.261,-12.505 -10.25,-22.358c-0.827,-18.667 -2.148,-48.492 -2.847,-64.253c-0.228,-5.152 8.494,-13.354 13.316,-14.383c7.282,2.026 16.722,3.823 18.165,9.99c4.696,20.077 7.478,82.576 7.852,112.409c0.71,10.166 14.178,20.636 18.306,29.953c4.659,10.516 6.873,24.211 10.466,31.873c1.899,4.05 9.884,2.976 8.572,11.043c0.179,4.193 -5.205,9.337 -6.606,15.602c-2.03,9.08 0.012,26.165 -4.099,35.673c-3.868,8.946 -14.805,17.345 -21.462,19.884c-5.762,2.197 -14.855,1.153 -18.033,-4.132c-3.693,-6.14 -9.687,-28.804 -4.121,-32.709Z"/> <path d="M350.562,750.847c-0.513,4.001 -8.843,0.958 -12.101,-0c-3.01,-0.886 -5.927,-3.01 -7.451,-5.753c-2.915,-5.25 -3.894,-19.012 -10.043,-25.746c-6.15,-6.734 -21.226,-6.154 -26.853,-14.659c-8.155,-12.327 -20.371,-49.29 -22.077,-59.302c-0.665,-3.898 9.502,-3.959 11.839,-0.769c5.452,7.441 10.715,35.962 20.871,45.413c10.156,9.451 23.711,9.393 40.065,11.293c7.636,10.136 6.809,41.269 5.75,49.523Z"/> <path d="M108.047,1725.67c0.382,-0.984 -0.106,-2.094 -1.09,-2.476l-47.976,-18.647c-0.984,-0.383 -2.094,0.106 -2.476,1.09l-2.854,7.342c-0.383,0.985 0.106,2.095 1.09,2.477l47.975,18.647c0.985,0.383 2.094,-0.106 2.477,-1.09l2.854,-7.343Z"/> <path d="M114.609,1708.82c0.382,-0.984 -0.106,-2.094 -1.091,-2.477l-47.975,-18.647c-0.984,-0.382 -2.094,0.106 -2.477,1.091l-2.853,7.342c-0.383,0.984 0.106,2.094 1.09,2.477l47.975,18.647c0.984,0.382 2.094,-0.106 2.477,-1.091l2.854,-7.342Z"/> <path d="M356.34,1027.26c-12.649,2.953 -26.918,3.254 -41.671,2.759" style="fill:none;"/> <path d="M152.956,231.964l0.547,65.258c0,0 7.25,4.937 7.463,7.593c0.545,6.761 -7.137,8.387 -7.137,8.387l-0.181,21.632l53.046,1.278c-0,-0 15.741,2.819 15.495,4.291c-0.247,1.471 -16.973,4.539 -16.973,4.539l-51.648,-0.483l-0.686,82.357c8.8,-3.954 26.077,-6.282 31.695,-1.065c5.603,5.202 8.917,12.544 8.998,21.198c0.115,12.27 -4.966,24.054 -14.637,26.912c-6.906,2.04 -19.853,1.549 -19.853,1.549l-0.105,154.727l72.747,1.012l-0.228,12.588l-72.141,0.701l-0.194,52.166c-0,0 11.615,-4.284 24.305,-1.038c8.052,2.059 10.061,9.839 11.009,18.481c0.815,7.421 -2.559,19.991 -11.574,22.964c-11.145,3.675 -23.943,0.324 -23.943,0.324l0.707,163.136c29.621,-0.129 60.951,-0.121 80.932,-0.351c16.805,-0 31.829,2.075 33.109,3.191c-1.177,1.479 -22.33,3.684 -34.135,3.882c-23.241,0.205 -45.833,0.486 -80.077,0.789l0.401,45.375l129.489,-3.043l0.671,-14.812l15.165,0.021c-0,-0 -0.621,10.694 0.738,13.275c1.132,2.148 5.286,1.079 5.469,3.499c0.228,3.017 -1.958,12.735 -1.958,12.735l-156.709,1.953l0.393,88.436c-0,0 24.684,-1.308 29.658,2.458c6.861,5.194 11.505,19.501 8.124,25.425c-6.836,11.98 -33.031,9.029 -33.031,9.029l-2.06,122.415c0,0 17.354,-2.657 21.961,4.008c4.07,5.887 7.236,11.871 6.228,27.276c-0.302,4.613 -5.579,12.709 -10.202,12.709c-4.502,0 -9.545,-8.603 -13.908,-8.741c-5.554,-0.175 -6.013,5.999 -12.84,5.784c-4.555,-0.143 -13.299,-2.774 -12.905,-15.257c0.109,-3.471 6.008,-8.51 6.008,-8.51l1.482,-135.302c0,-0 -13.797,3.867 -16.213,-11.906c-0.641,-4.184 7.884,-12.092 7.884,-12.092l0.536,-45.345l3.497,-0.407l-0.639,-104.126l-0.367,-596.165c-24.807,0.354 -45.586,0.651 -45.586,0.651l-1.886,-9.664c-0,-0 -18.426,-3.391 -22.218,-5.841c-2.485,-1.606 -3.439,-8.296 -0.535,-8.859c11.696,-2.271 70.71,-4.762 70.71,-4.762l-0.988,-67.014c-1.672,2.403 -9.635,-8.562 -7.236,-16.144c0.341,-4.624 7.001,-9.952 11.999,-10.79c10.15,-1.702 14.627,6.599 19.719,6.994c7.563,-1.268 17.744,-4.081 22.991,-0.501c5.246,3.58 8.364,11.003 7.346,17.147c-1.018,6.145 -3.957,16.081 -8.404,18.93c-3.642,2.333 -16.35,4.196 -20.387,2.644c-5.624,-2.161 -7.882,-17.724 -7.882,-17.724"/> <path d="M182.448,82.914c-0.758,2.527 -1.273,8.145 2.039,10.356c4.867,3.247 14.971,4.971 26.583,6.373c2.091,5.097 -1.564,16.973 1.953,17.398c3.518,0.425 16.713,-9.732 20.629,-16.19c3.915,-6.458 2.731,-15.244 2.378,-20.828c-0.085,-1.344 0.31,-3.344 -1.768,-3.213c-2.948,0.186 -3.368,3.163 -7.71,2.968c-3.037,-0.137 -3.817,-2.599 -5.847,-2.69c-6.388,-0.287 -24.965,0.152 -31.351,0.777c-2.172,0.212 -6.279,2.959 -6.906,5.049Z"/> <path d="M136.414,124.616c30.463,-7.671 64.227,-9.182 103.46,-0.372" style="fill:none;"/> <path d="M137.067,83.335c5.336,-0.603 27.44,-5.364 47.631,-3.181" style="fill:none;"/> <path d="M136.414,167.203c37.441,-8.139 72.269,-8.325 103.46,2.559" style="fill:none;"/> <path d="M213.638,1929.51l-0,25.996c-0,5.407 1.868,6.929 7.056,6.929l16.483,0c2.408,0 0.383,-3.91 1.657,-5.72c0.786,-1.117 1.968,-0.08 1.968,-1.54l0,-26.523c0,-0.913 0.881,-1.453 0.554,-2.251c1.089,-1.449 1.734,-3.239 1.734,-5.177l-0,-28.611c-0,-7.012 -2.372,-8.798 -7.276,-8.798l-19.384,0.073c-4.905,-0 -8.522,3.909 -8.522,8.725l-0.059,30.837c-0,3.858 2.25,4.909 5.789,6.06Z"/> </g> <g id="Keys"> <g id="LeftPalmKeys"> <path id="HighD" d="M395.099,695.691c0.186,-16.171 -7.133,-55.804 -12.397,-55.877c-2.263,-0.031 -4.345,1.07 -4.25,3.44c0.614,15.2 1.408,70.327 1.033,85.87c-0.046,1.881 3.185,6.879 4.954,6.899c3.575,0.041 10.278,-6.986 10.66,-40.332Z"/> <path id="HighF" d="M359.415,757.096c0.185,-16.17 -7.134,-55.804 -12.397,-55.876c-2.264,-0.031 -4.346,1.069 -4.25,3.44c0.613,15.2 1.407,70.327 1.032,85.869c-0.045,1.881 3.185,6.88 4.954,6.9c3.576,0.041 10.278,-6.987 10.661,-40.333Z"/> <path id="HighEFlat" d="M363.743,621.469c0.185,-14.763 -7.42,-47.298 -12.683,-47.364c-2.264,-0.029 -4.156,0.976 -4.25,3.14c-0.602,13.942 1.344,66.934 2.342,81.049c0.121,1.714 2.208,4.587 3.645,3.645c2.323,-1.523 10.563,-10.027 10.946,-40.47Z"/> </g> <g id="RightPalmKeys"> <path id="AddtlFSharp" d="M87.324,1466.57c7.678,-0.036 30.774,4.669 37.584,8.293c4.232,2.252 3.27,8.943 3.721,13.004c0.388,3.5 -1.564,6.351 -1.575,9.872c-0.032,10.88 1.653,42.213 -0.158,53.617c-1.366,8.599 -12.607,9.515 -16.218,17.582c-2.907,6.496 -2.237,11.451 -6.447,20.857c-2.074,4.634 -23.168,5.127 -29.874,4.806c-3.663,-0.176 -8.202,-2.896 -8.876,-6.501c-1.367,-7.305 -0.751,-28.374 0.675,-37.328c1.498,-9.4 9.513,-8.599 10.967,-17.727c2.021,-12.684 -0.321,-46.928 1.157,-58.378c0.518,-4.013 4.998,-8.078 9.044,-8.097Z"/> <path id="PalmBFlat" d="M54.302,1359.45c-0,-5.459 -2.135,-9.623 -7.594,-9.623c-6.647,2.19 -14.745,2.468 -21.289,0.37c-5.46,-0 -9.534,5.402 -9.534,10.861l0.559,46.122c0.144,10.112 7.687,18.104 20.376,18.104c8.686,-0 16.021,-5.811 16.021,-11.27l1.461,-54.564Z"/> <path id="PalmC" d="M50.027,1266.99c0,-4.742 -3.85,-8.592 -8.592,-8.592l-16.628,-0.278c-4.742,0 -8.592,3.85 -8.592,8.592l-0,61.778c-0,4.742 3.622,7.645 8.592,8.592l16.762,2.746c4.545,0.745 8.458,-3.43 8.458,-8.172l0,-64.666Z"/> <path id="HighE" d="M40.044,1171.93c0,-5.443 -4.419,-9.863 -9.862,-9.863l-19.726,0c-7.853,-0.483 -9.123,5.772 -9.123,11.215c1.12,4.182 7.326,4.86 11.016,8.711c1.282,1.338 2.218,2.799 2.474,4.448c2.282,14.672 0.62,27.34 0.62,50.729c0,5.444 4.42,9.863 9.863,9.863l16.256,0.72c5.444,0 9.863,-4.419 9.863,-9.863c1.723,-28.615 -0.057,-53.774 -11.381,-65.96Z"/> </g> <g id="PinkyPad"> <path id="PinkyBb" d="M462.085,1002.52c-12.405,2.484 -37.246,6.955 -65.429,12.218c-2.175,0.406 -4.203,1.745 -4.258,2.823c-0.748,14.862 -0.025,14.712 -0.762,29.345c-0.923,18.323 31.224,46.509 75.857,36.489c5.581,0.043 7.387,-8.861 7.437,-9.87c0.918,-18.292 2.798,-64.771 1.245,-67.968c-1.079,-2.223 -5.416,-4.773 -14.09,-3.037Z"/> <ellipse id="PinkyGSharp"  cx="425.115" cy="900.506" rx="37.833" ry="24.467"/> <path id="PinkyCSharp" d="M464.29,925.178c-5.839,1.874 -16.923,4.88 -30.221,8.761c-1.026,0.299 -1.899,1.53 -1.83,2.595c0.945,14.69 2.098,32.618 3.057,47.537c0.205,3.194 3.536,7.467 6.663,8.147c8.082,1.758 22.775,-0.879 29.404,-2.256c2.609,-0.719 3.823,-2.903 3.759,-3.9c-1.164,-18.08 -2.644,-53.968 -3.684,-57.209c-0.723,-2.253 -3.066,-4.985 -7.148,-3.675Z"/> <path id="PinkyB" d="M389.236,931.12c6.049,1.008 17.46,2.434 31.18,4.346c1.059,0.147 2.059,1.243 2.187,2.302c1.703,14.013 4.224,34.761 5.838,48.046c0.386,3.176 -2.369,6.834 -5.364,7.96c-7.743,2.911 -26.613,6.986 -32.697,7.567c-1.688,0.161 -2.44,-1.085 -2.521,-2.082c-1.469,-18.057 -5.722,-60.109 -5.162,-63.467c0.388,-2.333 2.31,-5.376 6.539,-4.672Z"/> </g> <path id="Octave" d="M69.767,496.775c2.992,-3.273 13.311,-0.957 12.847,3.453c-1.534,14.547 -2.493,43.379 -5.383,59.336c-0.605,4.377 -12.696,14.975 -21.177,30.143c-8.63,15.434 -10.021,33.089 -16.593,29.676c-8.716,-4.526 -9.569,-45.962 -3.961,-66.225c5.615,-20.287 26.094,-47.443 34.267,-56.383Z"/> <circle id="D" cx="184.764" cy="1619.77" r="28.922"/> <circle id="E" cx="195.318" cy="1526.24" r="28.922"/> <circle id="F" cx="186.63" cy="1386.13" r="28.922"/> <circle id="G" cx="324.174" cy="837.377" r="28.922"/> <circle id="C" cx="290.193" cy="776.846" r="28.922"/> <circle id="B" cx="274.48" cy="655.796" r="28.922"/> <circle id="AddtlHighF" cx="260.167" cy="593.826" r="23.028"/> <ellipse id="BFlat" cx="287.951" cy="711.008" rx="20.829" ry="20.8"/> <path id="DSharp" d="M54.135,1695.53l3.672,-9.449c1.309,-3.366 5.104,-5.037 8.47,-3.728l49.973,19.423c3.366,1.308 5.037,5.103 3.728,8.47l-3.673,9.449l13.532,5.259c3.506,-10.395 6.982,-20.797 10.268,-29.893c7.057,-19.529 2.101,-26.726 -2.117,-33.589c-9.147,-14.885 -46.796,-29.976 -61.286,-26.702c-5.845,1.321 -11.945,5.181 -14.528,10.587c-4.124,8.63 -11.544,27.446 -18.46,46.123l10.421,4.05Z"/> <path id="LowC" d="M51.823,1701.48l-10.311,-4.008c-5.636,15.437 -10.641,30.016 -12.819,37.762c-1.68,5.976 -0.809,13.505 3.046,18.37c9.897,12.491 39.997,25.449 52.258,27.382c11.972,1.887 24.73,-1.619 28.503,-9.525c4.727,-9.904 10.007,-24.882 15.292,-40.454l-13.798,-5.363l-3.776,9.714c-1.308,3.366 -5.103,5.037 -8.469,3.728l-49.973,-19.423c-3.366,-1.308 -5.037,-5.104 -3.728,-8.47l3.775,-9.713Z"/> </g> </g> </svg>',
                width: pageSize.width / 2, // 0.8 * (pageSize.height - headerHeight - footerHeight),
                absolutePosition: { 
                  x: pageSize.width / 5 * 3, 
                  y: ( pageSize.height - headerHeight - footerHeight) / 2 + headerHeight
                },
              },  
            );
          }
          return [ 
              {
                  canvas: [ 
                      { 
                          type: 'rect', 
                          x: 0, 
                          y: 0, 
                          w: pageSize.width, 
                          h: headerHeight, 
                          lineColor: boxColor, 
                          color: boxColor 
                      },
                      { 
                          type: 'rect', 
                          x: 0, 
                          y: pageSize.height - footerHeight, 
                          w: pageSize.width, 
                          h: footerHeight, 
                          color: boxColor
                      },
                  ]
              },
              {
                // if you specify width, svg will scale proportionally
                svg: EMEO_LOGO,
                width: 120,
                absolutePosition: { x: 40, y: 20 },
              },    
              ...image        
          ];
          
      },
    
      footer: function(currentPage:number, pageCount: number, pageSize: ContextPageSize) { 

        let pageWidth = pageSize.width - 40 - 50;

        return [
              {
                table: {
                  dontBreakRows: true,
                  widths: [ 150, pageWidth - 150 ],
                  headerRows: 0,
                  body: [
                    [
                      {   
                        text: currentPage.toString() + ' of ' + pageCount,
                        color: '#fff',
                      },  
                      { 
                        text: "Circuit " + circuitId, 
                        color: '#fff',
                        alignment: 'right',
                        fontSize: 12,
                      }
                    ]
                  ]   
                },
                layout: 'noBorders', 
                absolutePosition: { x: 40, y: 30 }
              } 
          ] as Content[];
      },
      
      header: function(currentPage: number, pageCount: number, pageSize: ContextPageSize) : Content {
          
          return [
            { 
              text: backup.name || '', 
              color: '#FFD63E',
              alignment: 'right',
              fontSize: 12,
              margin: [ 140, 25, 40, 0 ]
            },
            {   
              text: "Configuration from " + reportDate,
              color: '#FFD63E',
              alignment: 'right',
              margin: [ 140, 3, 40, 0 ]
            },  
          ];
      },
  
      content: [
        {text: 'Your EMEO', style: 'header'},
      ]
    };    

    this.updateProgress(0.2, processed, "Preparing Layout");
    let settings = await this.renderSettings(backup);
    (docDefinition.content as Content[]).push(...settings);

    this.updateProgress(0.3, processed, "Preparing Layout");
    let fingerings = await this.renderFingerings(instrument, backup, this.renderOnlyCustomFingerings);
    (docDefinition.content as Content[]).push(...fingerings);

    this.updateProgress(0.4, processed, "Preparing Layout");

    const tableLayouts = {
      exampleLayout: {
        hLineWidth: function (i: number, node: any) {
          if (i === 0 || i === node.table.body.length) {
            return 10;
          }
          return i === node.table.headerRows ? 2 : 1;
        },
        vLineWidth: function (i: number) {
          return 1;
        },
        hLineColor: function (i: number) {
          return i === 1 ? "#0ff00" : "#FF0000";
        },
        paddingLeft: function (i: number) {
          return i === 0 ? 0 : 8;
        },
        paddingRight: function (i: number, node: any) {
          return i === node.table.widths.length - 1 ? 0 : 8;
        }
      }
    };

    this.updateProgress(0.5, processed, "Preparing Layout");

    let pdfDoc = pdfMake.createPdf(docDefinition, tableLayouts, fonts);

    if (this.fs.electronMode) {
      pdfDoc.getBase64(
        (b64document: string) => {
          window.ipcRenderer.send("window:showPDFBase64", "EMEO Configuration Report", b64document);
        },
        {
          progressCallback: (progress) => { 
                              this.updateProgress(progress, processed, "Rendering PDF"); 

                              if (progress === 1) {
                                this.updateProgress(1, 0, "Done");
                                this.cancelReport();
                              }
                            }
        }
      );
    }
    else {
      pdfDoc.open(
        {
          progressCallback: (progress) => { 
                              this.updateProgress(progress, processed, "Rendering PDF"); 

                              if (progress === 1) {
                                this.updateProgress(1, 0, "Done");
                                this.cancelReport();
                              }
                            }
        }
      );
    }
  }

  private _progressMessage: string = " ";

  get progressMessage(): string {
    return this._progressMessage;
  }

  protected _progress: number = 0;

  get progress() : string {
    let result = (this._progress * 100).toFixed(0);
    return result;
  }

  private updateProgress(progress: number, processed: number = 0, info: string) {

    this._progressMessage = info;
    this._progress = (1-processed) * progress + processed;

    this.cd.detectChanges();
  }

  private resetProgress() {

    this._progressMessage = "Waiting";
    this._progress = 0;

    this.cd.detectChanges();
  }
  private async renderSettings(backup: Backup) : Promise<any[]> {

    var content : any[] = [
      ...(await this.renderInstrumentInfo(backup)),
      ...(await this.renderGeneralSettings(backup)),
      ...(await this.renderMidiSettings(backup)),
      /* TODO: Disabled, may come back later
      ...(await this.renderDetectionSettings(backup)),
      */
    ];

    return content;
  }

  private buildSettingsTable(rows: any[] ) : Content {

    let table : Content = {
      margin: [5, 2],
      table: {
        dontBreakRows: true,
        widths: [ 200, 'auto' ],
        headerRows: 0,
        body: [
          ...rows
        ]
      },
      layout: 'noBorders'            
    }; 

    return table;
  }

  private async renderInstrumentInfo(backup: Backup): Promise<any[]> {

    if (backup.device) {
      let rows = [] as any[];

      let di: DeviceInfo = backup.device
      var it: InstrumentType;

      var description = "EMEO Digital Saxophone";

      switch (di.hardware.type) {
        case HardwareTypes.SaxHall: {
          description = "EMEO Digital Saxophone MK1";
          break;
        }

        case HardwareTypes.SaxHallWithSound: {
          description = "EMEO Digital Saxophone MS";
          break;
        }

        case HardwareTypes.Clarinet: {
          description = "EMEO Digital Clarinet";
          break;
        }
        
        default:
          description = "(invalid setting)";
      }

      for (var i = 0; i < INSTRUMENT_TYPES.length; i++) {
        var it = INSTRUMENT_TYPES[i];

        if ((it.type == di.hardware.type) && (it.version == di.hardware.version)) {
          description = it.title;
          break;
        }
      }

      rows.push(
        [
          { text: 'Instrument type', bold: true },
          { text: description}
        ]
      );

      if (di.hardware.serialNumber && di.hardware.serialNumber != "UNDEFINED" ) {

        rows.push(
          [
            { text: 'Serial Number', bold: true },
            { text: ( di.hardware.serialNumber ) }
          ]
        );
      }

      if (di.hardware.circuitSerialNumber) {

        rows.push(
          [
            { text: 'Circuit Serial Number', bold: true },
            { text: ( di.hardware.circuitSerialNumber ) }
          ]
        );
      }

      if (di.firmwareVersion) {

        rows.push(
          [
            { text: 'Firmware Version', bold: true },
            { text: ( di.firmwareVersion ) }
          ]
        );
      }

      if (di.ninaVersion) {

        rows.push(
          [
            { text: 'Bluetooth Firmware Version', bold: true },
            { text: ( di.ninaVersion ) }
          ]
        );
      }

      return [
        {text: 'Instrument Information', style: 'subheader'},
        this.buildSettingsTable(rows)
      ]
    }

    return [];
  }

  private async renderGeneralSettings(backup: Backup): Promise<any[]> {

    if (backup.globalSettings) {
      let rows = [] as any[];

      let gs: GlobalSettings = backup.globalSettings;

      if (gs.advancedModeEnabled) {

        rows.push(
          [
            { text: 'Advanced Mode', bold: true },
            { text: "ACTIVE", bold: true }
          ]
        );

        rows.push(
        [
          { text: 'Audio feedback', bold: true },
          { text: ( gs.audioFeedbackEnabled ? "ON" : "OFF" ) }
        ]
      );

      }

      rows.push(
        [
          { text: 'Instrument Key', bold: true },
          { text: ( gs.keyShift === 0 ? "C Concert" :
                  ( gs.keyShift === 1 ? [ "E", { text: "\u266D", font: "Accidentials" } ] :
                  ( gs.keyShift === 2 ? [ "B", { text: "\u266D", font: "Accidentials" } ] : "(invalid setting)"))) }
        ]
      );

      rows.push(
        [
          { text: 'Octave shift', bold: true },
          { text: ( gs.octaveShift === 0 ? "OFF" : 
                  ( gs.octaveShift < 0   ? "One octave down" :
                  ( gs.octaveShift > 0   ? "One octave up " : "(invalid setting)" ))) }
        ]
      );

      rows.push(
        [
          { text: 'Audio FX', bold: true },
          { text: ( gs.reverbEnabled ? "Enabled" : "OFF" ) }
        ]
      );

      rows.push(
        [
          { text: 'Pressure resistance', bold: true },
          { text: ( gs.breathResistanceLevel === 0  ? "Soft" : 
                  ( gs.breathResistanceLevel === 1  ? "Medium" :
                  ( gs.breathResistanceLevel === 2  ? "Hard" : "(invalid setting)" ))) }
        ]
      );

      rows.push(
        [
          { text: 'Pressure resistance offset', bold: true },
          { text: gs.pressureResistanceOffset }
        ]
      );
        	
      rows.push(
        [
          { text: 'Invalid note feedback', bold: true },
          { text: ( gs.invalidNoteFeedback === 0  ? "Stop playing" :
                  ( gs.invalidNoteFeedback === 1  ? "Continue last legal fingering" :
                  ( gs.invalidNoteFeedback === 2  ? "Disturb breath" : "(invalid setting)" ))) }
        ]
      );

      rows.push(
        [
          { text: 'Play without blow level', bold: true },
          { text: gs.playWithoutBlowLevel }
        ]
      );

      rows.push(
        [
          { text: 'When no keys are pressed in Play-without-Blow mode', bold: true },
          { text: (gs.pwbRootNoteEnabled ? "Silence" : "Play C#") }
        ]
      );

      if (gs.goSleepMultiplier) {
        rows.push(
          [
            { text: 'Power-Save timeout', bold: true },
            { text: ( gs.goSleepMultiplier === 127  ? "OFF" :
                    ( gs.goSleepMultiplier === 0  ? "20 Minutes" :
                    ( gs.goSleepMultiplier === 1  ? "40 Minutes" :
                    ( gs.goSleepMultiplier === 2  ? "60 Minutes" :
                    ( gs.goSleepMultiplier === 3  ? "120 Minutes" : "(invalid setting)" ))))) }
          ]
        );
      }

      return [
        {text: 'General Settings', style: 'subheader'},
        this.buildSettingsTable(rows)
      ]
    }

    return [];
  }

  private getCCDescription(code: number) : string {

    let ccType = MidiCCTypes.find( (type) => { return (type.code === code) });

    return code + " - " + ccType?.name || "(invalid value)";
  }

  private async renderDetectionSettings(backup: Backup): Promise<any[]> {

    if (backup.globalSettings) {
      let rows = [] as any[];

      let gs: GlobalSettings = backup.globalSettings;

      rows.push(
        [
          { text: 'Tonguing detection', bold: true },
          { text: (gs.tonguingDetectionEnabled ? "ON" : "OFF" ) }
        ]
      );

      if (gs.tonguingDetectionEnabled) {

        rows.push(
          [
            { text: 'Tonguing CC Number', bold: true },
            { text: this.getCCDescription(gs.tonguingCCNumber) }
          ]
        );

        rows.push(
          [
            { text: 'Tonguing sensitivity', bold: true },
            { text: gs.tonguingSensitivity }
          ]
        );        
      }
        	
      rows.push(
        [
          { text: 'Vocal detection', bold: true },
          { text: (gs.vocalDetectionEnabled ? "ON" : "OFF" ) }
        ]
      );

      if (gs.vocalDetectionEnabled) {
        rows.push(
          [
            { text: 'Vocal CC Number', bold: true },
            { text: this.getCCDescription(gs.vocalCCNumber) }
          ]
        );
      }

      rows.push(
        [
          { text: 'Overblow detection', bold: true },
          { text: (gs.overblowDetectionEnabled ? "ON" : "OFF" ) }
        ]
      );

      if (gs.overblowDetectionEnabled) {
        rows.push(
          [
            { text: 'Overblow CC Number', bold: true },
            { text: this.getCCDescription(gs.overblowCCNumber) }
          ]
        );        

        rows.push(
          [
            { text: 'Overblow pressure', bold: true },
            { text: ( gs.overblowPressure === 0 ? "Soft" :
                    ( gs.overblowPressure === 1 ? "Medium" :
                    ( gs.overblowPressure === 2 ? "Hard" : "(undefined value)"))) }
          ]
        );        

        rows.push(
          [
            { text: 'Overblow MIDI Channel', bold: true },
            { text: (gs.overblowMidiChannel + 1) }
          ]
        );        
      }

      return [
        {text: 'Detection Settings', style: 'subheader'},
        this.buildSettingsTable(rows)
      ]
    }

    return [];
  }

  private async renderMidiSettings(backup: Backup): Promise<any[]> {

    if (backup.globalSettings) {
      let rows = [] as any[];

      let gs: GlobalSettings = backup.globalSettings;

      rows.push(
        [
          { text: 'Channel', bold: true },
          { text: (gs.midiChannel + 1) }
        ]
      );
      rows.push(
        [
          { text: 'Breath aftertouch AT', bold: true },
          { text: (gs.breathAftertouchEnabled ? "ON" : "OFF" ) }
        ]
      );
      rows.push(
        [
          { text: 'Breath volume CC07', bold: true },
          { text: (gs.breathVolumeEnabled ? "ON" : "OFF" ) }
        ]
      );
        	
      rows.push(
        [
          { text: 'Breath expression CC11', bold: true },
          { text: (gs.breathExpressionEnabled ? "ON" : "OFF" ) }
        ]
      );

      return [
        {text: 'MIDI Settings', style: 'subheader'},
        this.buildSettingsTable(rows)
      ]
    }

    return [];
  }

  private getRightColumn(onlyCustomFingerings: boolean) : any {

    if (onlyCustomFingerings) {
      return {
        width: 'auto',

        text: 'Only custom fingerings',
        style: 'annotation',
      }
    }
    else {
      return {
        width: 'auto',

        table: {
          body: [
              [
                {
                  svg: '<svg viewBox="0 0 10 10" version="1.1" xmlns="http://www.w3.org/2000/svg"><circle cx="5" cy="6" r="4" style="fill:yellow;stroke-width:0.5pt;stroke:black;"/></svg>',
                  height: 12,
                  alignment: 'right'
                },
                {
                  text: 'Factory definition'
                },
              ],
              [
                {
                  svg: '<svg viewBox="0 0 10 10" version="1.1" xmlns="http://www.w3.org/2000/svg"><circle cx="5" cy="6" r="4" style="fill:blue;stroke-width:0.5pt;stroke:black;"/></svg>',
                  height: 12,
                  alignment: 'right'
                },
                {
                  text: 'User defined'
                }
              ]
          ],
        },
        
        layout: 'noBorders'
      }
    }
  }

  private async renderFingerings(instrument: InstrumentInfoEx, backup: Backup, onlyCustomFingerings: boolean) : Promise<any[]> {
  
    if (backup.unc) {

      var content: any[] = [
        {
          pageBreak: 'before',

          columns: [
            {
              width: '*',
              text: 'Fingerings', 
              style: 'subheader'  
            },

            this.getRightColumn(onlyCustomFingerings)
          ]
        }
      ];
  

      let unc = backup.unc;

      var records: UNCRecord[] = [];

      records = this.extractFingerings(unc.uncDump, records);
      records = this.extractFingerings(unc.ksrDump, records);
 
      if (onlyCustomFingerings) {
        records = records.filter( record => record.isCustomized );
      }
      
      records.sort( (left, right) => { 
        if (left.parameterType != right.parameterType) {
          return (left.parameterType - right.parameterType);
        }

        if (isDefaultKey(left.keyState)) {
          return -1;
        }

        if (isDefaultKey(right.keyState)) {
          return 1;
        }

        if (left.pitchShift != right.pitchShift) {
          return (left.pitchShift - right.pitchShift);
        }

        if (left.recordType == right.recordType) {
          return 0;
        }

        if (left.recordType == UNCRecordType.NOTE_CONFIG) {
          return -1;
        }

        if (left.recordType == UNCRecordType.NOTE_OVERBLOW && right.recordType != UNCRecordType.NOTE_CONFIG) {
          return 1;
        }

        if (left.recordType == UNCRecordType.NOTE_OVERBLOW && right.recordType != UNCRecordType.NOTE) {
          return -1;
        }

        if (left.recordType == UNCRecordType.NOTE && right.recordType != UNCRecordType.NOTE) {
          return 1;
        }

        return 0;
      });

      await this.renderKeyTable(instrument, content, records);

      return content;
    }
      
    return [];
  }

  private extractFingerings( dump: UNCDump, records: UNCRecord[] ) : UNCRecord[] {

    for (var i = 0; i < dump.recordCount; i++) {
        let record = dump.records[ i ] as UNCRecord;
  
        // Every fingering-combination can only occur once!
        if (record && (record.recordType != 0x01 && record.parameterType != 0 || record.isCustomized)) {
          // (record as any).index = keySignature(record.keyState);
          let duplicateKey = records.find( (other) => { return (keySignature(other.keyState) === keySignature(record.keyState)); });
          if (!duplicateKey) {
            records.push(record);
            (record as any).isFactoryOverwrite = (record.type == UNCType.USER_FACTORY);
          }
        }
      }

      return records;
  }
 
  async renderKeyTable(instrument: InstrumentInfoEx, content: any[], records: UNCRecord[]) {

    var instruments: any[] = [];
    var notes: any[] = [];
    var annotations: any[] = [];
    var pitchBending: any[] = [];
    var pressureResistance: any[] = [];

    var colSpec: any[] = [ ];
    var heights: any[] = [ 'auto', 'auto', 10, 10 ];

    const firstColWidth = 0;
    const colWidth = 50;
    const itemsPerRow = 9;
    const blocksPerPage = 3;
    
    var items = 0;
    var scoreBar: any = undefined;
    var blocks = 0;

    var noteList = [] as any[];

    for (var record of records) {

      if (items % itemsPerRow == 0) {
        colSpec = (firstColWidth > 0 ? [firstColWidth] : []);
        instruments = (firstColWidth > 0 ? [''] : []);
        annotations = [];
        notes = [];
        noteList = [];
        pressureResistance = [];
        pitchBending = [];
      }

      // Generate the instrument image 
      let uncType = record.type; 
      /*
                    (record.recordType === UNCRecordType.NOTE ? UNCType.FACTORY : 
                    (record.recordType === UNCRecordType.NOTE_CONFIG ? UNCType.USER :
                    (record.recordType === UNCRecordType.NOTE_OVERBLOW ? UNCType.USER : UNCType.INVALID)));
      */
                
      let htmlString = await this.renderFingering(instrument, uncType, decodeKeys(record.keyState, instrument, true), (record as any).isFactoryOverwrite || false);

      colSpec.push(colWidth);

      instruments.push( {
        svg: htmlString,
        height: 100,
      });

      // Generate annotation, if needed
      let stack = [] as any[];

      if (record.recordType === UNCRecordType.NOTE_OVERBLOW) {
        stack.push(
          {
            text: 'OVERBLOW',
            style: 'annotation',
            background: 'green',
            bold: true,
          }
        )
      }

      if (record.pitchShift && record.pitchShift != 8192) {
        pitchBending.push(
          {
            svg: await this.renderPitchScale(record.pitchShift),
            width: 50,
            height: 10,
          }
        )
      } 
      else {
        pitchBending.push( { text: '' });
      }
      
      if (record.resistance && record.resistance != 10) {
        pressureResistance.push(
          {
            svg: await this.renderPressureScale(record.resistance),
            width: 50,
            height: 10,
          });
      }
      else {
        pressureResistance.push( { text: '' });
      }

      annotations.push({
        stack: stack
      });

      // Generate the notes basic block
      if (items % itemsPerRow === 0) {
        scoreBar = { svg: '', width: 510, colSpan: itemsPerRow };
        notes.push(scoreBar);
      }
      else {
        notes.push( { text: '' } );
      }

      noteList.push( record.parameterType );

      items++;

      scoreBar.colSpan = colSpec.length;

      if (items % itemsPerRow == 0) {

        let notesBar = this.renderNotes( noteList, itemsPerRow, itemsPerRow );
        scoreBar.svg = notesBar.svg;
        scoreBar.width *= notesBar.relWidth;
        
        blocks++;

        (content as Content[]).push(
          {
            margin: [5, 2],
            pageBreak: ( blocks % blocksPerPage == 0 ? 'after' : undefined ),
            table: {
              dontBreakRows:true,
              widths: colSpec,
              heights: heights,
              headerRows: 0,
              body: [
                notes,
                instruments,
                pitchBending,
                pressureResistance,
              ]
            },
            layout: 'noBorders'            
          }); 
      }
    }

    if (items % itemsPerRow != 0) {

      let notesBar = this.renderNotes( noteList, items % itemsPerRow, itemsPerRow );
      scoreBar.svg = notesBar.svg;

      scoreBar.width *= notesBar.relWidth;

      (content as Content[]).push(
        {
          margin: [5, 2],
          table: {
            widths: colSpec,
            heights: heights,
            headerRows: 0,
            body: [
              notes, 
              instruments,
              pitchBending,
              pressureResistance,
            ],
          },
          layout: 'noBorders'
        });
    }
  }
  
  private renderNotes( notes: number[], expectedBlocks: number, blocksPerScore: number ) : { svg: string, relWidth: number } {

    const VF = Vex.Flow;
    const Renderer = VF.Renderer
    const Stave = VF.Stave
    const Formatter = VF.Formatter
    const StaveNote = VF.StaveNote

    const staveWidth = 150;
    const staveHeight = 170;
    const staveTop = 40;

    const maxSize = 10 + staveWidth * blocksPerScore;
    const expectedSize = 10 + staveWidth * expectedBlocks;

    // Create an SVG renderer and attach it to the DIV element with id="output".
    const div: HTMLDivElement = this.tempScore.nativeElement;
    div.innerHTML = '';
    const renderer = new Renderer(div, Renderer.Backends.SVG);

    // Configure the rendering context.
    renderer.resize(expectedSize, staveHeight);
    const context = renderer.getContext();

    // Measure 1
    const staveMeasure1 = new Stave(10, staveTop, staveHeight);
    staveMeasure1.addClef('treble');
    staveMeasure1.setWidth(staveWidth);

    var currentStave: any = staveMeasure1;
    var currentMeasure: any = undefined;
    var lastCode = 0;
    
    for (var code of notes) {

      if (code != lastCode) {
        let noteConfig = getMidiNoteForCode( code );

        if (noteConfig) {

          if (currentMeasure) {
            // Helper function to justify and draw a 4/4 voice
            currentStave.setContext(context).draw();
            Formatter.FormatAndDraw(context, currentStave, currentMeasure);

            currentMeasure = undefined;
            currentStave = new Stave(currentStave.getWidth() + currentStave.getX(), staveTop, staveHeight);
            currentStave.setWidth(staveWidth);
          }

          let staveNote = new StaveNote({ keys: [noteConfig.note + '/' + (noteConfig.oct + 3)], duration: 'w' });

          if (noteConfig.sharp == true) {
            staveNote = staveNote.addAccidental(0, new VF.Accidental('#'));
          }

          currentMeasure = [ staveNote ];
        }
      }
      else {
        currentStave.setWidth( currentStave.getWidth() + staveWidth);
      }

      lastCode = code;
    }
    
    if (currentMeasure && currentStave) {
      // Helper function to justify and draw a 4/4 voice
      currentStave.setContext(context).draw();
      Formatter.FormatAndDraw(context, currentStave, currentMeasure);
    }

    let result = { 
        svg: div.innerHTML, 
        relWidth: (currentStave.getX() + currentStave.getWidth()) / maxSize,
      };

    return result;
  }

}
